import React, { useEffect, useState, useRef, useCallback } from "react";
import "../../styles/live-subscriptions/membership-notification.css";
import notificationSound from "../../assets/vocal-sound/vocal_staff_out_of_range.mp3";
import { CallIcon, LocationIcon1, PersonIcon } from "../../images/icons/icons";
import { formatTime } from "./TrackingUtils";

const OutOfRangeWebhook = ({ userInfoData, onOutOfRangeDetected }) => {
  const audioRef = useRef(null);
  const wsRef = useRef(null);
  const wsBackoffRef = useRef(2000);
  const processedWebhookEvents = useRef(new Set());

  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

  const playNotificationSound = () => {
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }
    } catch (error) {}
  };

  const showOutOfRangeNotification = useCallback(
    (outOfRangeData) => {
      const eventId = `${outOfRangeData?.staff_id || ""}_${
        outOfRangeData?.staff_name || ""
      }_${outOfRangeData?.time || ""}_${Date.now()}`;

      const now = Date.now();
      const existingEvent = Array.from(processedWebhookEvents.current).find(
        (item) => {
          const [id, timestamp] = item.split("_TIMESTAMP_");
          return (
            id.includes(outOfRangeData?.staff_id || "") &&
            id.includes(outOfRangeData?.staff_name || "") &&
            now - parseInt(timestamp) < 10000
          );
        }
      );

      if (existingEvent) {
        return;
      }

      const eventWithTimestamp = `${eventId}_TIMESTAMP_${now}`;
      processedWebhookEvents.current.add(eventWithTimestamp);

      processedWebhookEvents.current.forEach((item) => {
        const [, timestamp] = item.split("_TIMESTAMP_");
        if (now - parseInt(timestamp) > 30000) {
          processedWebhookEvents.current.delete(item);
        }
      });

      const notification = {
        ...outOfRangeData,
        id: eventId,
        timestamp: Date.now(),
      };

      setNotifications((prev) => [notification, ...prev]);

      playNotificationSound();

      // Trigger stats refresh callback
      if (onOutOfRangeDetected) {
        onOutOfRangeDetected();
      }

      setTimeout(() => {
        setNotifications((prev) => {
          const updated = prev.filter((n) => n.id !== notification.id);
          if (activeTab >= updated.length && updated.length > 0) {
            setActiveTab(updated.length - 1);
          }
          return updated;
        });
      }, 95000);
    },
    [activeTab, onOutOfRangeDetected]
  );

  useEffect(() => {
    const WS_URL = process.env.REACT_APP_ODM_WSS_URL;

    if (!WS_URL || !userInfoData || isIOS()) {
      return;
    }

    let stopped = false;

    const computeChannels = () => {
      try {
        const acc = userInfoData?.account_type;
        const channels = [];

        if (acc === "receptionist" && userInfoData?.gym_id) {
          channels.push(`tracking:gym:${userInfoData.gym_id}`);
        }
        if (acc === "cluster_manager") {
          const userGyms = userInfoData?.gyms?.gyms || userInfoData?.gyms || [];
          if (Array.isArray(userGyms)) {
            userGyms.forEach((g) => {
              const gid = g?.value || g?.gym_id || g?.uid || g;
              if (gid) channels.push(`tracking:gym:${gid}`);
            });
          }
        }
        if (acc === "admin") {
          channels.push("tracking:global");
        }

        return Array.from(new Set(channels));
      } catch {
        return [];
      }
    };

    const connect = () => {
      if (stopped) return;
      try {
        const wsUrl = `${WS_URL}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        console.log("ws===>", ws);

        ws.onopen = () => {
          wsBackoffRef.current = 2000;
          const channels = computeChannels();

          console.log("channels===>", channels);
          try {
            ws.send(
              JSON.stringify({
                type: "SUBSCRIBE",
                channels: channels,
                event_type: ["staff_out_of_range"],
              })
            );
          } catch (e) {}
        };

        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);

            if (
              msg?.link_type === "outOfRangeScreen" ||
              msg?.status === "out_of_range" ||
              msg?.data?.event_type === "staff_out_of_range"
            ) {
              const staffData = msg.data?.data;
              if (staffData) {
                showOutOfRangeNotification(staffData);
              }
            }
          } catch (err) {}
        };

        ws.onclose = () => {
          if (stopped) return;
          const delay = Math.min(wsBackoffRef.current, 30000);
          setTimeout(connect, delay);
          wsBackoffRef.current = Math.min(wsBackoffRef.current * 2, 30000);
        };

        ws.onerror = () => {
          try {
            ws.close();
          } catch {}
        };
      } catch (error) {
        const delay = Math.min(wsBackoffRef.current, 30000);
        setTimeout(connect, delay);
        wsBackoffRef.current = Math.min(wsBackoffRef.current * 2, 30000);
      }
    };

    connect();

    return () => {
      stopped = true;
      try {
        if (wsRef.current && wsRef.current.readyState === 1) {
          wsRef.current.close();
        }
      } catch {}
    };
  }, [userInfoData, showOutOfRangeNotification]);

  useEffect(() => {
    if (audioRef.current) {
      const audio = audioRef.current;
      audio.addEventListener("loadstart", () => {});
      audio.addEventListener("canplay", () => {});
      audio.addEventListener("canplaythrough", () => {});
      audio.addEventListener("error", () => {});
      audio.load();
    }
  }, []);

  useEffect(() => {
    if (notifications.length > 0) {
      setActiveTab(0);
    }
  }, [notifications.length]);

  return (
    <>
      <audio
        ref={audioRef}
        preload="auto"
        src={notificationSound}
        style={{ display: "none" }}
      />

      {notifications.length > 0 && (
        <div className="live-subscription-membership-notifications-container">
          <div className="live-subscription-membership-notifications-wrapper">
            <div className="live-subscription-feed-modal">
              <div className="live-subscription-modal-header">
                <div>
                  <h2 className="live-subscription-modal-title">
                    ⚠️ Out of Range Alert
                  </h2>
                  <p className="live-subscription-active-count">
                    {notifications.length} Staff Out of Range
                  </p>
                </div>
                <button
                  className="live-subscription-close-modal-btn"
                  onClick={() => setNotifications([])}
                >
                  ×
                </button>
              </div>

              <div className="live-subscription-tab-navigation">
                {notifications.map((notification, index) => {
                  return (
                    <button
                      key={notification.id}
                      className={`live-subscription-tab-item ${
                        index === activeTab ? "active" : ""
                      }`}
                      onClick={() => setActiveTab(index)}
                    >
                      #{index + 1}
                    </button>
                  );
                })}
              </div>

              {notifications[activeTab] && (
                <div className="live-subscription-membership-notification-card">
                  <div className="live-subscription-member-info-section">
                    <div className="live-subscription-member-basic-info">
                      <div className="live-subscription-member-name">
                        <div className="live-subscription-member-name-left">
                          <PersonIcon />
                          {notifications[activeTab]?.staff_name || "NA"}
                        </div>
                        <span
                          className="tb-badge"
                          style={{
                            backgroundColor: "#dc3545",
                            color: "white",
                            padding: "4px 12px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          Out of Range
                        </span>
                      </div>
                      <div className="live-subscription-member-phone">
                        <CallIcon />
                        Staff ID: {notifications[activeTab]?.staff_id || "NA"}
                      </div>
                      <div className="live-subscription-gym-location">
                        <LocationIcon1 />
                        {notifications[activeTab]?.gym_name || "NA"}
                      </div>
                    </div>
                  </div>

                  <div className="live-subscription-subscription-details">
                    <div className="live-subscription-detail-item">
                      <div className="live-subscription-detail-label">
                        Staff Type
                      </div>
                      <div className="live-subscription-detail-value">
                        {notifications[activeTab]?.staff_type?.toUpperCase() ||
                          "-"}
                      </div>
                    </div>

                    <div className="live-subscription-detail-item">
                      <div className="live-subscription-detail-label">
                        Current Distance
                      </div>
                      <div className="live-subscription-detail-value">
                        <span style={{ color: "#dc3545", fontWeight: "600" }}>
                          {notifications[activeTab]?.distance || "0"}m
                        </span>
                      </div>
                    </div>

                    <div className="live-subscription-detail-item">
                      <div className="live-subscription-detail-label">
                        Max Allowed Distance
                      </div>
                      <div className="live-subscription-detail-value">
                        {notifications[activeTab]?.max_distance || "0"}m
                      </div>
                    </div>

                    <div className="live-subscription-detail-item">
                      <div className="live-subscription-detail-label">
                        Out by Distance
                      </div>
                      <div className="live-subscription-detail-value">
                        <span style={{ color: "#dc3545", fontWeight: "600" }}>
                          {(notifications[activeTab]?.distance || 0) -
                            (notifications[activeTab]?.max_distance || 0)}
                          m
                        </span>
                      </div>
                    </div>

                    <div className="live-subscription-detail-item span2">
                      <div className="live-subscription-detail-label">Time</div>
                      <div className="live-subscription-detail-value">
                        {notifications[activeTab]?.time
                          ? formatTime(notifications[activeTab]?.time)
                          : "-"}
                      </div>
                    </div>
                  </div>

                  <div className="live-subscription-membership-badges">
                    <div
                      className="live-subscription-membership-badge"
                      style={{
                        backgroundColor: "#dc3545",
                        color: "white",
                      }}
                    >
                      ⚠️ Alert: Staff Out of Allowed Range
                    </div>
                    <div
                      className="live-subscription-membership-badge"
                      style={{
                        backgroundColor: "#ffc107",
                        color: "#000",
                      }}
                    >
                      {notifications[activeTab]?.staff_type?.toUpperCase() ||
                        "STAFF"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OutOfRangeWebhook;
